//
// DragDropLayoutTreeItem
//
// Drag and Drop wrapper for LayoutTreeItem

import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { useDebounce } from '../hooks/useDebounce'
import { DragTypes } from './DragTypes'

export const DragDropLayoutTreeItem = observer(function DragDropLayoutTreeItem({
    tree,
    treeitem,
    onDrop,
    children,
}) {
    const ref = useRef(null)

    const [isoverzone, setIsoverzone] = useState('top')
    const [isMousedown, setIsMousedown] = useState(false)

    const debouncedIsoverzone = useDebounce(isoverzone, 50)

    const [{ isDragging }, drag] = useDrag({
        type: DragTypes.LAYOUT_COMPONENT,
        item: {
            type: DragTypes.LAYOUT_COMPONENT,
            id: treeitem.id,
            parentid: treeitem.parentid,
            layoutgid: treeitem.item.layoutgid,
        },
        collect: monitor => ({
            isDragging: !!monitor.isDragging(),
        }),
        end: (item, monitor) => {
            if (ref.current) {
                setIsMousedown(false)
            }
        },
    })

    const onMouseDown = e => {
        if (e.button === 0) {
            setIsMousedown(true)
        }
    }
    const onMouseUp = e => {
        setIsMousedown(false)
    }

    const canDrop = dragitem => {
        if (!ref.current || !dragitem) {
            return false
        }
        if (dragitem.type === DragTypes.PIM_CLASS) {
            return true
        }
        if (dragitem.type === DragTypes.PIM_FIELD) {
            return true
        }
        // else, it is another LAYOUT_COMPONENT
        if (
            dragitem.parentid === 0 ||
            !treeitem.parentid ||
            dragitem.id === treeitem.id ||
            tree.inPath(dragitem, treeitem) // is Descendant
        ) {
            return false
        }
        return true
    }

    const [{ isOver }, drop] = useDrop({
        accept: [DragTypes.LAYOUT_COMPONENT, DragTypes.PIM_CLASS, DragTypes.PIM_FIELD],
        canDrop: dragitem => canDrop(dragitem),

        drop(dragitem, monitor) {
            onDrop(dragitem, treeitem, isoverzone)
            return {
                receiver: 'DragDropLayoutTreeItem',
                dragitem: dragitem,
                treeitem: treeitem,
                dropzone: isoverzone,
            }
        },

        collect: monitor => {
            let isOver = !!monitor.isOver()
            const dragitem = monitor.getItem()
            if (!canDrop(dragitem)) {
                isOver = false
            }
            return {
                isOver: isOver,
            }
        },

        hover(dragitem, monitor) {
            if (!canDrop(dragitem)) {
                // console.log("no-canDrop")
                return
            }
            if (treeitem.item.isContainer || treeitem.item.isComponentContainer) {
                // can drop inside
                // isComponentContainer can only accept when empty
                if (treeitem.item.isComponentContainer) {
                    if (treeitem.item.root || treeitem.item.layout) {
                        setIsoverzone('disabled')
                        return
                    } else {
                        setIsoverzone('inside')
                        return
                    }
                } else if (treeitem.item.parent && treeitem.item.parent.root) {
                    setIsoverzone('inside')
                    return
                }
                // determine inside, top or bottom
                const itemBoundingRect = ref.current.getBoundingClientRect()
                const zoneSize = Math.floor(itemBoundingRect.height / 3)
                const topZone = zoneSize
                const bottomZone = zoneSize
                const itemTopY = topZone
                const itemBottomY =
                    itemBoundingRect.bottom - itemBoundingRect.top - bottomZone
                const mousePosition = monitor.getClientOffset()
                const itemMouseY = mousePosition.y - itemBoundingRect.top
                if (itemMouseY < itemTopY) {
                    setIsoverzone('top')
                } else if (itemMouseY > itemBottomY && !tree.isExpanded(treeitem.id)) {
                    setIsoverzone('bottom')
                } else {
                    setIsoverzone('inside')
                }
            } else {
                // can only drop above or below
                const itemBoundingRect = ref.current.getBoundingClientRect()
                const itemTopY = Math.floor(
                    (itemBoundingRect.bottom - itemBoundingRect.top) / 2
                )
                const mousePosition = monitor.getClientOffset()
                const itemMouseY = mousePosition.y - itemBoundingRect.top
                if (itemMouseY < itemTopY) {
                    setIsoverzone('top')
                } else {
                    setIsoverzone('bottom')
                }
            }
        },
    })

    drag(drop(ref))

    let dragclassname = ''
    if (isOver && debouncedIsoverzone === 'top') dragclassname = 'drag-over-top'
    else if (isOver && debouncedIsoverzone === 'inside')
        dragclassname = 'drag-over-inside'
    else if (isOver && debouncedIsoverzone === 'bottom')
        dragclassname = 'drag-over-bottom'
    if (isMousedown) dragclassname += ' maybe-start-dragging'

    useEffect(() => {
        // set style later - cloned drag preview has original style
        if (ref && ref.current) {
            ref.current.style.opacity = isDragging ? '0.1' : '1'
        }
    }, [isDragging])

    return (
        <div
            ref={ref}
            className={dragclassname}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
        >
            {children}
        </div>
    )
})
